// 검사 로그 패널 — 실시간 스캔 결과 + DB 실제 데이터, 상태/정책 필터 + CSV 내보내기 + 상세 모달
import { useEffect, useMemo, useState } from 'react';
import { LOG_DATA, type LogStatus, type LogRow } from '../lib/seedData';
import { downloadCsv, toCsv } from '../lib/csv';
import { useUI } from '../lib/uiStore';
import { useScanStore, type ScanJob, type ScanStatus } from '../lib/scanStore';
import { adminGet, adminDelete } from '../lib/adminApi';
import { formatSize } from '../lib/fileUtils';

interface ApiScanJob {
  id: string;
  fileId: string;
  apiClientId: string;
  filename: string;
  sizeBytes: number;
  sha256: string | null;
  mimeType: string | null;
  policyId: string;
  status: string;
  engineResults: Record<string, unknown> | null;
  threatLabel: string | null;
  createdAt: string;
}

function apiJobToLogRow(j: ApiScanJob): LogRow {
  const status: LogStatus = j.status === 'safe' ? 'safe' : j.status === 'quarantined' ? 'quarantine' : 'blocked';
  const engines = j.engineResults
    ? Object.entries(j.engineResults)
        .map(([k, v]) => `${k}: ${(v as Record<string, unknown>).verdict ?? v}`)
        .join(', ')
    : '-';
  return {
    fid: j.fileId,
    name: j.filename,
    size: formatSize(j.sizeBytes),
    policy: j.policyId,
    project: j.apiClientId,
    engines,
    status,
    time: new Date(j.createdAt).toLocaleString('ko-KR'),
  };
}

const BADGE: Record<LogStatus, { cls: string; label: string }> = {
  safe: { cls: 'bp-safe', label: 'SAFE' },
  quarantine: { cls: 'bp-quarantine', label: 'QUARANTINE' },
  blocked: { cls: 'bp-blocked', label: 'BLOCKED' },
};

function scanStatusToLogStatus(s: ScanStatus): LogStatus {
  if (s === 'safe') return 'safe';
  if (s === 'quarantine') return 'quarantine';
  return 'blocked'; // blocked, failed, pending, scanning → blocked로 표시
}

function scanJobToLogRow(j: ScanJob): LogRow {
  return {
    fid: j.fileId,
    name: j.filename,
    size: j.size,
    policy: j.policy,
    project: '직접 검사',
    engines: j.engines,
    status: scanStatusToLogStatus(j.status),
    time: j.time ?? '',
  };
}

export function Logs() {
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | LogStatus>('all');
  const [policyFilter, setPolicyFilter] = useState('all');
  const { openModal, showToast } = useUI();
  const liveJobs = useScanStore((s) => s.jobs);

  const [dbRows, setDbRows] = useState<LogRow[]>([]);
  const [dbFileIds, setDbFileIds] = useState<Set<string>>(new Set());
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  async function loadDbJobs() {
    try {
      const data = await adminGet<{ items: ApiScanJob[] }>('/admin/scan-jobs?limit=200');
      const rows = data.items.map(apiJobToLogRow);
      setDbRows(rows);
      setDbFileIds(new Set(data.items.map((j) => j.fileId)));
    } catch {
      // 조용히 무시 — 토큰 없는 환경에서도 시드 데이터는 표시
    }
  }

  useEffect(() => { void loadDbJobs(); }, []);

  function handleDelete(fid: string, name: string) {
    openModal({
      title: '🗑 스캔 기록 삭제',
      sub: `<strong>${name}</strong>의 스캔 기록을 삭제합니다.`,
      body: '<div>이 작업은 되돌릴 수 없습니다.</div>',
      confirmLabel: '삭제',
      onConfirm: () => {
        if (dbFileIds.has(fid)) {
          adminDelete(`/admin/scan-jobs/${fid}`)
            .then(() => {
              setDeletedIds((p) => new Set([...p, fid]));
              showToast(`${name} 삭제 완료`, 'success');
            })
            .catch(() => showToast('삭제 실패', 'error'));
        } else {
          setDismissedIds((p) => new Set([...p, fid]));
          showToast(`${name} 삭제 완료`, 'success');
        }
      },
    });
  }

  function openDetail(r: LogRow) {
    const statusColor = r.status === 'safe' ? 'var(--accent)' : r.status === 'quarantine' ? 'var(--yellow)' : 'var(--red)';
    const statusLabel = r.status === 'safe' ? 'SAFE' : r.status === 'quarantine' ? 'QUARANTINE' : 'BLOCKED';
    const row = (label: string, value: string) =>
      `<div style="display:flex;align-items:flex-start;gap:0;padding:10px 0;border-bottom:1px solid var(--border)">` +
      `<div style="min-width:100px;flex-shrink:0;color:var(--text3);font-size:12px;font-family:'JetBrains Mono',monospace;text-transform:uppercase;letter-spacing:.06em;padding-top:2px">${label}</div>` +
      `<div style="flex:1;font-size:13px;word-break:break-all;color:var(--text)">${value}</div>` +
      `</div>`;
    openModal({
      title: `📄 ${r.name}`,
      sub: `<span style="font-family:'JetBrains Mono',monospace;font-size:12px;color:var(--text3)">${r.fid}</span>`,
      bodyPlain: true,
      body:
        `<div style="border-top:1px solid var(--border)">` +
        row('파일명', `<strong>${r.name}</strong>`) +
        row('File ID', `<span style="font-family:'JetBrains Mono',monospace;font-size:12px">${r.fid}</span>`) +
        row('크기', r.size) +
        row('정책', `<span style="background:var(--surface3);padding:2px 8px;border-radius:4px;font-family:monospace;font-size:12px">${r.policy}</span>`) +
        row('프로젝트', `<span style="color:var(--blue)">${r.project}</span>`) +
        row('엔진 결과', r.engines || '-') +
        row('상태', `<strong style="color:${statusColor}">${statusLabel}</strong>`) +
        row('검사 시각', r.time || '-') +
        `</div>`,
      confirmLabel: '확인',
    });
  }

  // 실시간 → DB 실제 데이터 → 시드 데이터 순으로 표시
  const liveFileIds = useMemo(() => new Set(liveJobs.map((j) => j.fileId)), [liveJobs]);
  const visibleDbRows = useMemo(
    () => dbRows.filter((r) => !deletedIds.has(r.fid) && !liveFileIds.has(r.fid)),
    [dbRows, deletedIds, liveFileIds],
  );
  const allRows = useMemo(() => {
    const live = liveJobs
      .filter((j) => j.status !== 'pending' && j.status !== 'scanning' && !dismissedIds.has(j.fileId))
      .map(scanJobToLogRow);
    const seed = LOG_DATA.filter((r) => !dismissedIds.has(r.fid));
    return [...live, ...visibleDbRows, ...seed];
  }, [liveJobs, visibleDbRows, dismissedIds]);

  const filtered = useMemo(() => {
    return allRows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (policyFilter !== 'all' && r.policy !== policyFilter) return false;
      return true;
    });
  }, [allRows, statusFilter, policyFilter]);

  const policyOptions = Array.from(new Set(allRows.map((r) => r.policy)));

  function exportCsv() {
    const csv = toCsv(
      ['file_id', 'filename', 'size', 'policy', 'project', 'engines', 'status', 'time'],
      filtered.map((r) => ({
        file_id: r.fid, filename: r.name, size: r.size, policy: r.policy,
        project: r.project, engines: r.engines, status: r.status, time: r.time,
      })),
    );
    downloadCsv(`fileguard-logs-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  }

  const liveCount = liveJobs.filter((j) => j.status !== 'pending' && j.status !== 'scanning').length;

  return (
    <>
      <div className="sect-head">
        <div className="sect-title">
          📋 검사 로그 <em>전체 {filtered.length}건</em>
          {liveCount > 0 && (
            <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--accent)', background: 'var(--accent-dim)', padding: '2px 7px', borderRadius: 4, fontFamily: 'JetBrains Mono,monospace' }}>
              +{liveCount} 실시간
            </span>
          )}
        </div>
        <div className="flex-row">
          <button className="btn btn-ghost btn-sm" onClick={exportCsv}>📥 CSV 내보내기</button>
          <button className="btn btn-pri btn-sm" onClick={() => setFilterOpen((v) => !v)}>
            {filterOpen ? '✕ 필터 닫기' : '+ 필터 추가'}
          </button>
        </div>
      </div>

      {filterOpen && (
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginBottom: 16,
            padding: 14,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            alignItems: 'center',
          }}
        >
          <label className="form-label" htmlFor="status-filter" style={{ margin: 0 }}>상태 필터</label>
          <select
            id="status-filter"
            className="form-input"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | LogStatus)}
          >
            <option value="all">전체</option>
            <option value="safe">SAFE</option>
            <option value="quarantine">QUARANTINE</option>
            <option value="blocked">BLOCKED</option>
          </select>

          <label className="form-label" htmlFor="policy-filter" style={{ margin: '0 0 0 16px' }}>정책 필터</label>
          <select
            id="policy-filter"
            className="form-input mono"
            style={{ width: 160 }}
            value={policyFilter}
            onChange={(e) => setPolicyFilter(e.target.value)}
          >
            <option value="all">전체</option>
            {policyOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            <option value="none-such">(존재하지 않음)</option>
          </select>
        </div>
      )}

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>File ID</th>
              <th>파일명</th>
              <th>크기</th>
              <th>정책</th>
              <th>호출 프로젝트</th>
              <th>엔진 결과</th>
              <th>상태</th>
              <th>시각</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 28, textAlign: 'center', color: 'var(--text3)' }}>결과가 없습니다</td></tr>
            ) : (
              filtered.map((r, idx) => {
                const b = BADGE[r.status];
                return (
                  <tr key={`${r.fid}_${idx}`}>
                    <td><span className="text-mono" style={{ color: 'var(--text3)' }}>{r.fid}</span></td>
                    <td><span style={{ fontSize: 13 }}>{r.name}</span></td>
                    <td className="text-muted text-mono">{r.size}</td>
                    <td><span className="ext-chip">{r.policy}</span></td>
                    <td className="text-mono" style={{ color: 'var(--blue)', fontSize: 12 }}>{r.project}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{r.engines}</td>
                    <td><span className={`badge-pill ${b.cls}`}>{b.label}</span></td>
                    <td className="text-mono text-muted">{r.time}</td>
                    <td style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openDetail(r)}>상세</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r.fid, r.name)}>삭제</button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
